import PropTypes from "prop-types";
import Box from "@mui/material/Box";

/** Thin stroke icons — modern/clean, uses currentColor. */
function StrokeIcon({ children, sx, size = 22, strokeWidth = 1.6 }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      sx={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
        ...sx,
      }}
    >
      <g
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {children}
      </g>
    </Box>
  );
}

StrokeIcon.propTypes = {
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
};

StrokeIcon.defaultProps = {
  sx: null,
  size: 22,
  strokeWidth: 1.6,
};

function withStroke(paths) {
  function Icon({ sx, fontSize }) {
    const size = typeof fontSize === "number" ? fontSize : 22;
    return (
      <StrokeIcon sx={sx} size={size}>
        {paths}
      </StrokeIcon>
    );
  }
  Icon.propTypes = {
    sx: PropTypes.object,
    fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };
  Icon.defaultProps = {
    sx: null,
    fontSize: 22,
  };
  return Icon;
}

export const IconHome = withStroke(
  <>
    <path d="M4 10.5 12 4l8 6.5" />
    <path d="M6.5 9.75V20h11V9.75" />
  </>,
);

export const IconShop = withStroke(
  <>
    <path d="M5 8h14l-1.2 11.2a1.5 1.5 0 0 1-1.5 1.3H7.7a1.5 1.5 0 0 1-1.5-1.3L5 8Z" />
    <path d="M9 8a3 3 0 0 1 6 0" />
  </>,
);

export const IconCart = withStroke(
  <>
    <path d="M3.5 5.5h1.8l1.4 10.2a1.4 1.4 0 0 0 1.4 1.2h8.7a1.4 1.4 0 0 0 1.4-1.15L19.5 8H7" />
    <circle cx="9.2" cy="19.2" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="16.3" cy="19.2" r="1.15" fill="currentColor" stroke="none" />
  </>,
);

export const IconUser = withStroke(
  <>
    <circle cx="12" cy="8" r="3.25" />
    <path d="M5.5 19.25c1.6-3.1 4-4.65 6.5-4.65s4.9 1.55 6.5 4.65" />
  </>,
);

export const IconSearch = withStroke(
  <>
    <circle cx="11" cy="11" r="6" />
    <path d="m16.2 16.2 3.3 3.3" />
  </>,
);

export const IconMenu = withStroke(
  <>
    <path d="M5 8h14" />
    <path d="M5 12h14" />
    <path d="M5 16h14" />
  </>,
);

export const IconLogin = withStroke(
  <>
    <path d="M10 5.5H6.5A1.5 1.5 0 0 0 5 7v10a1.5 1.5 0 0 0 1.5 1.5H10" />
    <path d="M12.5 12H20" />
    <path d="m16.5 8.5 3.5 3.5-3.5 3.5" />
  </>,
);

export const IconDash = withStroke(
  <>
    <rect x="4.5" y="4.5" width="6.5" height="6.5" rx="1.2" />
    <rect x="13" y="4.5" width="6.5" height="6.5" rx="1.2" />
    <rect x="4.5" y="13" width="6.5" height="6.5" rx="1.2" />
    <rect x="13" y="13" width="6.5" height="6.5" rx="1.2" />
  </>,
);

export const IconJobs = withStroke(
  <>
    <path d="M8.5 8.5 5.2 11.8a2.2 2.2 0 0 0 3.1 3.1L11.5 12" />
    <path d="M12.5 11.5 15.8 8.2a2.2 2.2 0 1 0-3.1-3.1L9.5 8.2" />
    <path d="m14.5 14.5 4.2 4.2" />
  </>,
);

export const IconStock = withStroke(
  <>
    <path d="M4.5 8.2 12 4.5l7.5 3.7v7.6L12 19.5l-7.5-3.7V8.2Z" />
    <path d="M12 19.5v-7.6" />
    <path d="M4.5 8.2 12 11.9l7.5-3.7" />
  </>,
);

export const IconPipeline = withStroke(
  <>
    <rect x="4" y="5" width="4.5" height="14" rx="1.1" />
    <rect x="9.75" y="5" width="4.5" height="14" rx="1.1" />
    <rect x="15.5" y="5" width="4.5" height="14" rx="1.1" />
  </>,
);

export const IconGrid = withStroke(
  <>
    <rect x="4.5" y="4.5" width="6" height="6" rx="1.1" />
    <rect x="13.5" y="4.5" width="6" height="6" rx="1.1" />
    <rect x="4.5" y="13.5" width="6" height="6" rx="1.1" />
    <rect x="13.5" y="13.5" width="6" height="6" rx="1.1" />
  </>,
);

export const IconBag = withStroke(
  <>
    <path d="M6.5 7.5h11v11.5a1.5 1.5 0 0 1-1.5 1.5h-8a1.5 1.5 0 0 1-1.5-1.5V7.5Z" />
    <path d="M9 7.5V6.2A2.2 2.2 0 0 1 11.2 4h1.6A2.2 2.2 0 0 1 15 6.2V7.5" />
  </>,
);

/** Shield + check (legacy). Prefer IconQrScanner for registration. */
export const IconShield = withStroke(
  <>
    <path d="M12 3.5 5.5 6.2v5.4c0 4.1 2.7 7.2 6.5 8.9 3.8-1.7 6.5-4.8 6.5-8.9V6.2L12 3.5Z" />
    <path d="m9.2 12.1 1.8 1.8 3.8-3.8" />
  </>,
);

/** QR code — valve-body sale / install registration scanner (matches native app). */
export const IconQrScanner = withStroke(
  <>
    <path d="M5 8V5.8A.8.8 0 0 1 5.8 5H8" />
    <path d="M16 5h2.2a.8.8 0 0 1 .8.8V8" />
    <path d="M19 16v2.2a.8.8 0 0 1-.8.8H16" />
    <path d="M8 19H5.8a.8.8 0 0 1-.8-.8V16" />
    <rect x="7" y="7" width="4.2" height="4.2" rx="0.6" />
    <rect x="12.8" y="7" width="4.2" height="4.2" rx="0.6" />
    <rect x="7" y="12.8" width="4.2" height="4.2" rx="0.6" />
    <path d="M13 13h1.6v1.6H13zM15.4 13H17v1.6h-1.6zM13 15.4h1.6V17H13zM15.4 15.4H17V17h-1.6z" />
  </>,
);
